import { useMemo, useState } from 'react';

import { useUserRolePatch, useUsers } from '../../api/admin';
import type { UserDto, UserSortState } from '../../api/admin';
import { ApiError } from '../../api/client';
import { useAuth } from '../../auth/AuthContext';
import { roleLabel } from '../../auth/roles';
import type { Role } from '../../auth/roles';
import { Combobox } from '../../components/Combobox';
import { Badge, Button, EmptyState, ErrorState, Select } from '../../components/ui';
import {
  DEPARTMENT_BUCKET_LABEL,
  DEPARTMENT_SHORT,
  departmentFilterMetaLabel,
  userDepartmentFilterOptions,
} from '../../departments';
import type { DepartmentBucket, ManagerDepartment } from '../../departments';
import { TableWrap } from '../../components/TableWrap';
import { formatDateTime } from '../dashboard/format';
import { useVenueDirectory } from '../quotas/dictionary';
import { venueComboboxOptions } from '../quotas/quotas-model';
import {
  LAST_ADMIN_DEMOTION_HINT,
  ROLE_EXPLAINER,
  ROLE_OPTIONS,
  auditActionCount,
  blockedLabel,
  departmentBucketOf,
  curatorVenueCount,
  hasActiveUserFilters,
  isLastAdminDemotion,
  isMe,
  managedVenueCount,
  parseUsers,
  toggleUserSort,
  userLabel,
  userIdsOfVenue,
  userSortArrow,
  usersMatchingFilters,
  venueLinksKnown,
  type UserListFilters,
} from './settings-model';
import { UserCreateDrawer } from './UserCreateDrawer';
import { UserDrawer } from './UserDrawer';

/**
 * И8 — users & roles (contract #25, ADMIN-only, task T.C5; the drawer is T.X4).
 *
 * The role editor is a plain select per row that PATCHes on change; the api
 * writes the `ENTITY_CHANGE` audit row in the same transaction, which is what
 * the note under the table says out loud — the client adds no audit of its own.
 *
 * The 409 gets a line of its own: "demoting the LAST admin" is the one refusal
 * that reads as a rule rather than a failure, and the operator must see which
 * row it was about. The client checks it first (a courtesy — the server is the
 * authority), so the round trip only happens for a change that can succeed.
 *
 * T.X4 — the row is a BUTTON now, not just a line: clicking it opens the right
 * drawer (name, e-mail, role, block, venues). Everything the drawer does is also
 * visible on the row, so the table stays the overview. Wave 9 (T.Y9) added the
 * create button above the table; wave 10 (A13, T.Z10) removed the sign-in
 * provider badge — every account signs in BOTH ways since D21.
 *
 * Wave 10 (T.Z13, A10–A12):
 *   * the «Кто есть кто» callout — three lines, one per role, so a newcomer stops
 *     guessing what a Куратор is before creating one;
 *   * two filters over the roster — by venue (a combobox over the #6 directory;
 *     a venue matches the users its `managerUserIds` OR `curatorUserIds` carry)
 *     and by role. BOTH ARE CLIENT-SIDE ON PURPOSE: #25 answers the WHOLE roster
 *     in one read and has no server-side filters for it, so a filter change is a
 *     re-slice of the cache, never a second request;
 *   * the count columns are named by what they count — «Offline площадки» (the
 *     `venue_managers` links, the offline funnel) and «Online площадки» (the
 *     `venue_curators` links, the online cabinet).
 *
 * ADDITIVE (v1.9.53, T.Z177) — the «Действий в журнале» column: how many rows of the
 * audit trail name this account (`UserDto.auditActions`, ONE aggregate on the #25
 * read). Its header is the tab's first SORT that goes to the api: a click refetches
 * #25 with `?sortBy=auditActions&order=asc|desc` — the register's control, a fresh
 * read instead of a client-side slice — and zeros render as the plain 0 they are.
 */
export function UsersTab() {
  const me = useAuth();
  const patch = useUserRolePatch();
  const directory = useVenueDirectory();

  const [rowError, setRowError] = useState<Record<number, string | undefined>>({});
  const [openId, setOpenId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [filters, setFilters] = useState<UserListFilters>({ role: null, venueId: null, department: null });
  // ADDITIVE (v1.9.53, T.Z177) — the audit-count sort lives on the api (#25
  // `?sortBy=auditActions&order=`), like every other ordering this app shows; local
  // state here, because this tab's filters are local state too (see the header note).
  // `null` sends no parameters at all — the api answers its frozen roster order.
  const [sort, setSort] = useState<UserSortState>(null);
  const users = useUsers(sort);

  const rows = useMemo(() => parseUsers(users.data), [users.data]);
  const directoryRows = useMemo(() => directory.data ?? [], [directory.data]);

  // The two filters cut the roster locally — see the header note. The selected
  // venue's user ids are computed for the meta line, not for the filter itself.
  const visible = useMemo(
    () => usersMatchingFilters(rows, filters, directoryRows),
    [rows, filters, directoryRows],
  );
  const open = openId === null ? undefined : rows.find((user) => user.id === openId);
  const filtersActive = hasActiveUserFilters(filters);
  const selectedVenueIds =
    filters.venueId === null ? null : userIdsOfVenue(directoryRows, filters.venueId);
  // An older api answers the directory without the link sets: a venue filter
  // then has nothing true to match, and the page says so instead of showing an
  // empty table that would read as «this venue has no people».
  const venueLinksUnknown = filters.venueId !== null && !venueLinksKnown(directoryRows);

  const changeRole = (user: UserDto, next: Role) => {
    if (next === user.role) return; // a no-op select must not become a PATCH
    setRowError((current) => ({ ...current, [user.id]: undefined }));

    if (isLastAdminDemotion(user, next, rows)) {
      setRowError((current) => ({ ...current, [user.id]: LAST_ADMIN_DEMOTION_HINT }));
      return;
    }

    patch.mutate(
      { userId: user.id, role: next },
      {
        onError: (error) => {
          setRowError((current) => ({ ...current, [user.id]: describe(error, user.id) }));
        },
      },
    );
  };

  if (users.isPending) {
    return (
      <div className="card" data-testid="users-skeleton">
        <div className="skeleton skeleton--line skeleton--wide" />
        <div className="skeleton skeleton--line" />
        <div className="skeleton skeleton--line" />
      </div>
    );
  }

  if (users.isError) {
    return (
      <div className="card">
        <ErrorState
          message={users.error instanceof Error ? users.error.message : 'Не удалось загрузить пользователей.'}
          onRetry={() => void users.refetch()}
        />
      </div>
    );
  }

  return (
    <div className="card card--flush" data-testid="users-panel">
      {/* A11 — the legend first, the table second: the person reading this page is
          about to CREATE an account, and the role is the one decision that shapes
          everything the account can reach. */}
      <section className="users-explainer" aria-label="Кто есть кто" data-testid="users-explainer">
        <h2 className="users-explainer__title">Кто есть кто</h2>
        <dl className="users-explainer__list">
          {ROLE_EXPLAINER.map((entry) => (
            <div className="users-explainer__row" key={entry.role} data-testid={`users-explainer-${entry.role}`}>
              <dt>{roleLabel(entry.role)}</dt>
              <dd>{entry.text}</dd>
            </div>
          ))}
        </dl>
      </section>

      {/* Wave 9 (T.Y9) — the create affordance lives above the table: an account is
          made BEFORE its first sign-in (Р21), so the empty roster must not read as
          "wait for people to show up" but as "nobody has been added yet". */}
      <div className="users-toolbar">
        <Button variant="primary" size="sm" data-testid="user-create-toggle" onClick={() => setCreating(true)}>
          + Добавить пользователя
        </Button>
        <span className="users-toolbar__hint">
          Создание аккаунта до первого входа (#25.1) — без письма-приглашения.
        </span>
      </div>

      {/* A10 — the two filters. Client-side (see the header note); the venue list
          comes from the same #6 directory the other filters use, so 189 venues
          arrive in one read and the combobox searches names AND addresses. */}
      <div className="users-filters" data-testid="users-filters">
        <div className="users-filters__field">
          <label className="field__label" htmlFor="users-filter-venue">
            Площадка
          </label>
          <Combobox
            id="users-filter-venue"
            testId="users-filter-venue"
            value={filters.venueId === null ? '' : String(filters.venueId)}
            options={venueComboboxOptions(directoryRows)}
            onChange={(next) =>
              setFilters((current) => ({ ...current, venueId: next === '' ? null : Number.parseInt(next, 10) }))
            }
            placeholder="Все площадки — найдите по названию или адресу"
            emptyText="Площадка не найдена"
            clearLabel="Показать пользователей всех площадок"
            resultsLabel={(count) => `Найдено площадок: ${count}`}
          />
        </div>
        <div className="users-filters__field">
          <label className="field__label" htmlFor="users-filter-role">
            Роль
          </label>
          <Select
            id="users-filter-role"
            data-testid="users-filter-role"
            value={filters.role ?? ''}
            onChange={(event) =>
              setFilters((current) => ({ ...current, role: event.target.value === '' ? null : (event.target.value as Role) }))
            }
          >
            <option value="">Все роли</option>
            {ROLE_OPTIONS.map((role) => (
              <option key={role} value={role}>
                {roleLabel(role)}
              </option>
            ))}
          </Select>
        </div>
        {/* ADDITIVE (T.Z42) — the «Отдел» filter, same client-side pattern as the
            two above: #25 carries the department on every row, so this is a
            re-slice of the cache, never a request. */}
        <div className="users-filters__field">
          <label className="field__label" htmlFor="users-filter-department">
            Отдел
          </label>
          <Select
            id="users-filter-department"
            data-testid="users-filter-department"
            value={filters.department ?? ''}
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                department: event.target.value === '' ? null : (event.target.value as DepartmentBucket),
              }))
            }
          >
            {userDepartmentFilterOptions().map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>
        <span className="users-toolbar__hint" data-testid="users-filter-meta">
          {venueLinksUnknown
            ? 'Список площадок пришёл без привязок — фильтр по площадке недоступен.'
            : `Показано ${visible.length} из ${rows.length}${departmentFilterMetaLabel(filters.department)} `}
        </span>
      </div>

      {visible.length === 0 ? (
        <EmptyState
          title={filtersActive ? 'Никого не найдено' : 'Пользователей нет'}
          hint={
            filtersActive
              ? 'По этому фильтру пользователей нет — ослабьте условие.'
              : 'Аккаунты создаются администратором — при первом входе через Яндекс или по коду на e-mail.'
          }
        />
      ) : (
        <TableWrap>
          <table className="data" data-testid="users-table">
            <thead>
              <tr>
                <th scope="col">Пользователь</th>
                <th scope="col">E-mail</th>
                {/* A13 (owner UX pass, 2026-09-12) — no «способ входа» column: every
                    account signs in BOTH ways since D21, so the badge said nothing. */}
                <th scope="col">Роль</th>
                {/* A12 — the columns are named by what they count: the manager links
                    are the offline funnel, the curator links the online cabinet. */}
                {/* ADDITIVE (T.Z42) — the manager's department, full name (the owner's
                    wording); «—» for everybody the owner has not filed (a КУРАТОР, an
                    АДМИН, an unfiled manager). */}
                <th scope="col" data-testid="users-column-department">
                  Отдел
                </th>
                <th scope="col" className="num">
                  Offline площадки
                </th>
                <th scope="col" className="num">
                  Online площадки
                </th>
                {/* ADDITIVE (v1.9.53, T.Z177) — the count sorts on the api: a header
                    click is a #25 refetch with `?sortBy=auditActions&order=`, the
                    register's control and arrow, not a client-side slice. */}
                <UserAuditSortHeader order={userSortArrow(sort)} onToggle={() => setSort(toggleUserSort(sort))} />
                <th scope="col">Последний вход</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((user) => {
                const error = rowError[user.id];
                const isSaving = patch.isPending && patch.variables?.userId === user.id;
                return (
                  <tr
                    key={user.id}
                    className="user-row"
                    data-testid="user-row"
                    data-user-id={user.id}
                    data-role={user.role}
                    data-blocked={String(user.blocked === true)}
                    aria-expanded={openId === user.id}
                    onClick={() => setOpenId(user.id)}
                  >
                    <td data-testid="user-name">
                      <button
                        type="button"
                        className="user-row__open"
                        data-testid={`user-open-${user.id}`}
                        onClick={(event) => {
                          // The row already opens the drawer; the button is the
                          // keyboard/AT door to the same place.
                          event.stopPropagation();
                          setOpenId(user.id);
                        }}
                      >
                        {userLabel(user)}
                      </button>
                      {blockedLabel(user) ? (
                        <span className="badge badge--bad" data-testid={`user-blocked-badge-${user.id}`}>
                          {blockedLabel(user)}
                        </span>
                      ) : null}
                    </td>
                    <td data-testid="user-email">{user.email}</td>
                    <td data-testid="user-role-cell">
                      <span className="user-role">
                        <Select
                          data-testid={`role-select-${user.id}`}
                          aria-label={`Роль ${userLabel(user)}`}
                          disabled={isSaving}
                          value={user.role}
                          // The select lives INSIDE a clickable row: the click that
                          // changes a role must not open the drawer over it.
                          onClick={(event) => event.stopPropagation()}
                          onChange={(event) => changeRole(user, event.target.value as Role)}
                        >
                          {ROLE_OPTIONS.map((role) => (
                            <option key={role} value={role}>
                              {roleLabel(role)}
                            </option>
                          ))}
                        </Select>
                        {isMe(me.user?.email, user.email) ? (
                          <Badge tone="neutral" title="Это ваш аккаунт">
                            это вы
                          </Badge>
                        ) : null}
                      </span>
                      {error ? (
                        <span className="user-role-error" role="alert" data-testid={`role-error-${user.id}`}>
                          {error}
                        </span>
                      ) : null}
                    </td>
                    <td
                      data-testid={`user-department-${user.id}`}
                      title={
                        user.role === 'MANAGER' && departmentBucketOf(user) !== 'NONE'
                          ? `${DEPARTMENT_BUCKET_LABEL[departmentBucketOf(user)]} (${DEPARTMENT_SHORT[departmentBucketOf(user) as ManagerDepartment]})`
                          : undefined
                      }
                    >
                      {departmentLabelCell(user)}
                    </td>
                    <td className="num" data-testid={`user-venue-count-${user.id}`}>
                      {managedVenueCount(user)}
                    </td>
                    <td className="num" data-testid={`user-curator-count-${user.id}`}>
                      {curatorVenueCount(user)}
                    </td>
                    {/* ADDITIVE (v1.9.53, T.Z177) — zero renders plainly: 0 actions is
                        an answer, not a gap (an older api also reads 0 here). */}
                    <td className="num" data-testid={`user-audit-actions-${user.id}`}>
                      {auditActionCount(user)}
                    </td>
                    <td data-testid="user-last-login">{formatDateTime(user.lastLoginAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TableWrap>
      )}

      {/* The meta line names the selected venue when the table is filtered by one —
          the count a venue filter answers is worth reading without opening the combobox. */}
      {filters.venueId !== null && selectedVenueIds !== null && venueLinksKnown(directoryRows) ? (
        <p className="settings-note" data-testid="users-filter-venue-note">
          {selectedVenueIds.manager.length + selectedVenueIds.curator.length} привязок к выбранной площадке (
          {selectedVenueIds.manager.length} менеджерских, {selectedVenueIds.curator.length} кураторских).
        </p>
      ) : null}

      <p className="settings-note" data-testid="users-note">
        Смена роли записывается в журнал аудита на сервере (действие ENTITY_CHANGE) — здесь она
        только отправляется. Клик по строке открывает карточку пользователя: имя, e-mail, роль, доступ,
        его площадки и кураторство.
      </p>

      {open ? <UserDrawer user={open} onClose={() => setOpenId(null)} /> : null}
      {creating ? <UserCreateDrawer onClose={() => setCreating(false)} /> : null}
    </div>
  );
}

/**
 * The «Отдел» cell: the FULL department name, or «—» for the rows the owner has not
 * filed. The short ОП/ОС codes are the storage spelling — they stay in the title
 * attribute, never as the primary text.
 */
function departmentLabelCell(user: Pick<UserDto, 'department' | 'role'>): string {
  if (user.role !== 'MANAGER') return '—';
  const bucket = departmentBucketOf(user);
  return bucket === 'NONE' ? '—' : DEPARTMENT_BUCKET_LABEL[bucket];
}

/**
 * ADDITIVE (v1.9.53, T.Z177) — the «Действий в журнале» header, the register's
 * sortable control (ApplicationsPage's `SortableHeader`, T.Z59): the button IS the
 * hit target, the arrow marks the active column and its direction, a muted idle
 * marker says "sortable" without claiming an order, and `aria-sort` carries the
 * same information where the spec puts it. One column sorts today — the vocabulary
 * (`?sortBy=auditActions`) lives in the api and the model's toggle.
 */
function UserAuditSortHeader({ order, onToggle }: { order: 'asc' | 'desc' | null; onToggle: () => void }) {
  return (
    <th className="num" aria-sort={order === 'asc' ? 'ascending' : order === 'desc' ? 'descending' : undefined}>
      <button
        type="button"
        className={order ? 'users-th-sort users-th-sort--active' : 'users-th-sort'}
        data-testid="sort-auditActions"
        data-order={order ?? undefined}
        title="Сортировать по «Действий в журнале»"
        onClick={onToggle}
      >
        <span>Действий в журнале</span>
        <span className={order ? 'users-th-sort__arrow' : 'users-th-sort__arrow users-th-sort__arrow--idle'} aria-hidden="true">
          {order === 'asc' ? '↑' : order === 'desc' ? '↓' : '↕'}
        </span>
      </button>
    </th>
  );
}

/** The api's 409 is the same rule; anything else keeps its own message. */
function describe(error: unknown, userId: number): string {
  if (error instanceof ApiError && error.is('CONFLICT', 409)) return LAST_ADMIN_DEMOTION_HINT;
  if (error instanceof ApiError && error.is('FORBIDDEN', 403)) {
    return `Недостаточно прав, чтобы менять роль пользователя ${userId}.`;
  }
  return error instanceof Error ? error.message : 'Не удалось сохранить роль.';
}
